import { Children, ReactNode, useLayoutEffect, useState, useRef, isValidElement } from 'react';
import useElementResizeObserver from '../useElementResizeObserver';
import NavTab from './NavTab';
import { LayoutGroup } from 'framer-motion';
import { useLocation } from 'react-router-dom';

import { HoverIndicator, SelectedIndicator } from './IndicatorComponents';

import CollapsedTabsList from './CollapsedTabList';

export type NavTabsProps = {
    // individual tab items (child elements)
    children: ReactNode[] | ReactNode;

    // whether to display entire tab as pill-type or default
    pill?: boolean;

    // disabled props (state and tooltip label) for the entire tab
    isDisabled?: boolean;
    isDisabledHint?: boolean;

    // entire tab's color mode; to adjust tab colors depending on container background
    colorMode?: 'dark' | 'light';

    // tab id; used for things that need unique tab id (eg, LayoutGroup)
    name: string;
};

function _NavTabs({ children, pill, isDisabled = false, isDisabledHint, colorMode = 'light', name }: NavTabsProps): JSX.Element {
    // width of the inline inner wrapper
    const tabInnerWrapper = useRef<HTMLDivElement>(null);
    const [, setTabInnerWrapperWidth] = useState(0); //

    // width of the block outer wrapper
    const tabOuterWrapper = useRef<HTMLDivElement>(null);
    const tabOuterWrapperSize = useElementResizeObserver(tabOuterWrapper);

    // total count of tab items, and count of tab items that are being shown
    const [tabItemsCount] = useState(Children.count(children));
    const [tabItemsShown, setTabItemsShown] = useState(tabItemsCount);

    // an array of numbers containing the width of each tab item
    const tabItemsWidths = useRef<number[]>([]);

    const gapOffset = 20; // gap offset for each tab item
    const rightOffset = 100; // space between the tablist and the element to its right

    // calculates the number of tab items to show based on each individual item's width
    function getTabItemsToShow(tabItemsWidths: number[], tabOuterWrapperWidth: number): number {
        let tabItemsToShow = 0;
        let totalItemsWidth = 0;

        for (let i = 0; i < tabItemsWidths.length; i++) {
            // increment "totalItemsWidth" variable by the width of each tab item
            totalItemsWidth = totalItemsWidth + tabItemsWidths[i] + gapOffset;

            // when "totalItemsWidth" variable becomes greater than the outerwrapper's width, break the loop and return how many "tabItemsToShow"
            if (totalItemsWidth + rightOffset > tabOuterWrapperWidth) {
                break;
            }

            // otherwise, continue the loop and add 1 item to the "tabItemsToShow"
            tabItemsToShow = tabItemsToShow + 1;
        }

        return tabItemsToShow;
    }

    useLayoutEffect(() => {
        if (tabInnerWrapper.current) {
            setTabInnerWrapperWidth(tabInnerWrapper.current.getBoundingClientRect().width);
            const tabItemsToShow = getTabItemsToShow(tabItemsWidths.current, tabOuterWrapperSize.width);
            setTabItemsShown(tabItemsToShow);
        }
    }, [tabOuterWrapperSize.width]);

    const location = useLocation();

    return (
        <div ref={tabOuterWrapper} className={`flex-grow rounded-md min-h-[36px] ${pill ? 'h-9' : 'h-full'} isolate w-full`}>
            <div ref={tabInnerWrapper} className={`inline-flex items-center ${isDisabled && 'cursor-not-allowed'} h-full w-full`}>
                <LayoutGroup id={name}>
                    <div className={`inline-flex h-full ${pill ? 'gap-2' : 'gap-4'}`}>
                        {Children.map(children, (child, index) => {
                            if (index + 1 <= tabItemsShown) {
                                return (
                                    <div
                                        ref={(e): void => {
                                            if (e) {
                                                tabItemsWidths.current = [...tabItemsWidths.current, e.getBoundingClientRect().width];
                                            }
                                        }}
                                    >
                                        {isValidElement(child) ? (
                                            <div className="relative group h-full flex items-center">
                                                <NavTab colorMode={colorMode} pill={pill} isDisabled={isDisabled} {...child.props}>
                                                    {child.props.children}
                                                </NavTab>

                                                {!child.props.isDisabled && <HoverIndicator pill={pill} colorMode={colorMode} />}
                                                {location.pathname.includes(child.props.link) && (
                                                    <SelectedIndicator pill={pill} colorMode={colorMode} />
                                                )}
                                            </div>
                                        ) : null}
                                    </div>
                                );
                            }
                        })}

                        {tabItemsShown < tabItemsCount && (
                            <CollapsedTabsList
                                pill={pill}
                                colorMode={colorMode}
                                childOffsetIndex={tabItemsShown}
                                tabItemsCount={tabItemsCount}
                                isDisabled={isDisabled}
                            >
                                {children}
                            </CollapsedTabsList>
                        )}
                    </div>
                </LayoutGroup>
            </div>
        </div>
    );
}

const NavTabs = Object.assign(_NavTabs, { Tab: NavTab });

export default NavTabs;
